import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { Alert } from '@mui/material';
import { AlertProps } from '@mui/lab';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as WarningIcon } from './assets/icons/warningIcon.svg';
import { ReactComponent as InfoIcon } from './assets/icons/InfoIcon.svg';

interface ErrorMessageProps extends AlertProps {
  title: string,
  message: string,
  link?: string,
  linkText?: string,
  linkWindow?: string,
  className?: string,
  hideCloseIcon?: boolean,
  showLink?: boolean,
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: '#ffffff',
      margin: "auto",
      boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
      [theme.breakpoints.up('md')]: {
        maxWidth: 870,
        marginBottom: 32
      },
      [theme.breakpoints.down('md')]: {
        maxWidth: 560,
        marginBottom: 32
      },
      [theme.breakpoints.down('sm')]: {
        width: "90%",
        marginBottom: 32
      },

      "& .MuiCollapse-container": {
        margin: "20px auto 44px",
        [theme.breakpoints.down('sm')]: {
          margin: "0 auto 20px"
        },
        "&.MuiCollapse-hidden": {
          margin: "0 auto 0",
        }
      },
      "& .MuiCollapse-wrapperInner": {
        [theme.breakpoints.down('md')]: {
          margin: "0"
        },
        [theme.breakpoints.down('sm')]: {
          margin: "0"
        },
        "& .MuiAlert-outlinedInfo": {
          border: "none",
        },
        "& .MuiAlert-outlinedError": {
          border: "none",
          justifyContent: 'center',
          "& .MuiAlert-icon": {
            margin: "5px 6px 0 0",
          },
          "& .MuiAlert-action": {
            [theme.breakpoints.up('md')]: {
              marginLeft: "0",
              padding: '0px 0px 0px 16px',
              alignItems: 'center'
            },
            [theme.breakpoints.down('sm')]: {
              position: "relative",
              top: "-15px",
              margin: "0",
              marginLeft: "auto",
              padding: '0px 0px 0px 16px',
              alignItems: 'center'
            },
          },
          "& .MuiAlert-message": {
            fontSize: "16px",
            padding: "13px 0",
            color: "#4A4A4A",
            "& a": {
              color: "#2d91e9",
              textDecoration: 'none'
            },
          },
          "& .MuiSvgIcon-fontSizeInherit": {
            fontSize: "20px"
          },
        },
      },
    },
    boldFont: {
      [theme.breakpoints.down('sm')]: {
        display: "block"
      }
    },
    copy: {
      [theme.breakpoints.down('sm')]: {
        marginLeft: "0",
        marginTop: 2,
        display: 'block',
      }
    }
  })
);

export default function ErrorMessage(props: ErrorMessageProps) {
  const classes = useStyles();
  const [open, setOpen] = React.useState<boolean>(true);
  const {
    icon,
    severity,
    title,
    message,
    link,
    linkText,
    linkWindow,
    className,
    hideCloseIcon,
    showLink = true,
    onClose } = props;
  const isLoggedOut = Boolean(sessionStorage.getItem('logged-out'));

  let messageLink: any = '';
  if (link && linkText) {
    messageLink = <Link target={linkWindow ? linkWindow : ''} to={link}>{linkText}</Link>
  }

  useEffect(() => {
    message && setOpen(true)
  }, [message]);

  return (
    <div className={open ? classnames(classes.root, className) : ''}>
      <Collapse in={open}>
        <Alert
          role="alert"
          icon={icon === 'error' ? <WarningIcon fontSize="inherit" /> : icon === 'info' ? <InfoIcon fontSize="inherit" /> : ''}
          variant="outlined"
          severity={severity}
          action={
            <IconButton
              data-testid="alertmsg"
              aria-label="close"
              color="inherit"
              size="small"
              onClick={(e) => {
                setOpen(false);
                isLoggedOut && sessionStorage.removeItem('logged-out');
                onClose && onClose(e);
              }}>
              {!hideCloseIcon ? (<CloseIcon fontSize="inherit" />) : null}
            </IconButton>
          }
        >
          <strong className={classes.boldFont}>{title}: &nbsp;</strong>
          <span className={classes.copy}>{message} {showLink && messageLink}</span>
        </Alert>
      </Collapse>
    </div>
  );
}
